import { Loader2 } from 'lucide-react';
import React from 'react';

import {
    type ConversationRecipient,
    conversationRecipientPhone,
} from '@apps/web/components/shared/messages/conversation_recipient_picker';
import { MessagesPane } from '@apps/web/components/shared/messages/messages_pane';
import { Button } from '@apps/web/components/ui/button';
import { useApiQuery } from '@apps/web/swr_data_fetching/use_api_query';

export function useConversationRecipientPreview(
    organizationId: string,
    recipient: ConversationRecipient | null,
) {
    const phoneNumber = conversationRecipientPhone(recipient);
    const preview = useApiQuery({
        path: '/organizations/{organization_id}/clients/recipient-preview',
        method: 'get',
        pathParams: { organization_id: organizationId },
        queryParams: { phone_number: phoneNumber ?? '' },
        enabled: phoneNumber !== null,
        keepPreviousData: false,
        select: (response) => response,
        reason: 'new-message-recipient-history',
    });
    return {
        ...preview,
        enabled: phoneNumber !== null,
        canSend:
            recipient !== null &&
            (phoneNumber === null ||
                (!!preview.data &&
                    !preview.error &&
                    !preview.data.unavailable_reason)),
    };
}

export function ConversationRecipientHistory({
    organizationId,
    timezone,
    preview,
}: {
    readonly organizationId: string;
    readonly timezone: string;
    readonly preview: ReturnType<typeof useConversationRecipientPreview>;
}) {
    if (!preview.enabled) {
        return <div className="min-h-0 flex-1" />;
    }
    if (preview.error) {
        return (
            <div
                className="flex min-h-0 flex-1 flex-col items-center justify-center gap-2 p-4 text-center text-sm"
                role="alert"
            >
                <p>Couldn’t check this number’s conversation.</p>
                <Button
                    onClick={() => {
                        void preview.mutate();
                    }}
                    size="sm"
                    type="button"
                    variant="outline"
                >
                    Try again
                </Button>
            </div>
        );
    }
    if (!preview.data) {
        return (
            <div
                className="text-muted-foreground flex min-h-0 flex-1 items-center justify-center gap-2 p-4 text-sm"
                role="status"
            >
                <Loader2 className="size-4 animate-spin" />
                Checking conversation…
            </div>
        );
    }
    const {
        conversation_id: conversationId,
        associations,
        unavailable_reason: unavailableReason,
        has_more_associations: hasMoreAssociations,
    } = preview.data;
    return (
        <div className="flex min-h-0 flex-1 flex-col">
            {unavailableReason ? (
                <p
                    className="text-destructive border-b px-4 py-3 text-sm"
                    role="alert"
                >
                    {unavailableReason}. Choose another number or recipient.
                </p>
            ) : null}
            {associations.length > 1 || hasMoreAssociations ? (
                <div className="bg-muted/50 border-b px-4 py-3 text-xs">
                    <p className="font-medium">Shared phone number</p>
                    <p className="text-muted-foreground mt-1 break-words">
                        {associations.join(' · ')}
                        {hasMoreAssociations ? ' · Additional profiles' : ''}
                    </p>
                    <p className="text-muted-foreground mt-1">
                        Messages to this number share the same conversation.
                    </p>
                </div>
            ) : null}
            {conversationId ? (
                <React.Fragment>
                    <p className="text-muted-foreground border-b px-4 py-2 text-xs">
                        Existing conversation with this number
                    </p>
                    <MessagesPane
                        conversationId={conversationId}
                        key={conversationId}
                        markAsRead={false}
                        organizationId={organizationId}
                        showComposer={false}
                        showRating={false}
                        timezone={timezone}
                    />
                </React.Fragment>
            ) : (
                <div className="text-muted-foreground flex min-h-0 flex-1 items-center justify-center p-5 text-center text-sm">
                    No messages with this number yet.
                </div>
            )}
        </div>
    );
}
